import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IBrand } from '../../models/brands.models';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { AddBrand, AddBrandSuccess, UpdateBrand, UpdateBrandSuccess } from '../../store/brands.actions';
import { Observable, Subject, combineLatest, map, of, startWith, take, takeUntil } from 'rxjs';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { numericAndAlphaOnlyValidator } from 'src/app/shared/validators/fields-validators';
import { FetchAllTags } from 'src/app/features/tags/store/tags.actions';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';

@Component({
  selector: 'app-add-brands',
  templateUrl: './add-brands.component.html',
  styleUrls: ['./add-brands.component.scss']
})
export class AddBrandsComponent implements OnInit, OnDestroy {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  form!: FormGroup;
  parent_id!: number;
  uploadedImage!: string;
  displayedImage!: string;
  tags$: Observable<ITag[]> = of([]);
  filteredTags$!: Observable<ITag[]>;
  tags: ITag[] = [];
  baseUrl = environment.BASE_URL_API;
  isUpdate: boolean = false;
  canSubmit = false;
  canSubmit$: Observable<boolean> = of(false);
  timestamp = new Date().getTime();
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AddBrandsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IBrand,
    private fb: FormBuilder,
    private store: Store<AppState>,private translateService: TranslateService,
    private actions$: Actions
  ) { }

  ngOnInit(): void {
    this.store.dispatch(FetchAllTags({}));
    this.tags$ = this.store.select((state) => state.tags.allTags);
    this.form = this.fb.group({
      code: new FormControl(this.data?.code ? this.data?.code : '', numericAndAlphaOnlyValidator()),
      name: new FormControl(this.data?.name ?? '', Validators.required),
      description: new FormControl(this.data?.description ?? ''),
      tagControl: new FormControl('')
    })
    if(this.data){
      this.displayedImage = this.data.image!;
      this.tags = this.data.tags!;
      this.isUpdate = true;
    }

    this.canSubmit$ = this.store.select(selectCanDo('product', this.isUpdate ? 'edit' : 'create'));
    this.canSubmit$.pipe(takeUntil(this.destroy$)).subscribe(canSubmit => {
      this.canSubmit = canSubmit;
      canSubmit ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false });
    });

    this.actions$.pipe(ofType(AddBrandSuccess, UpdateBrandSuccess), take(1)).subscribe(() => this.closed());

    this.filteredTags$ = combineLatest([
      this.tags$,
      this.form.get('tagControl')!.valueChanges.pipe(
        startWith('')
      ),
    ]).pipe(
      map(([tags, input]) => this._filterStrings(tags, input))
    );
  }

  private _filterStrings(strings: any[], value: string): any[] {
    const filterValue = this._normalizeValue(value);
    return strings.filter((string) =>
      this._normalizeValue(string.name)?.includes(filterValue!)
    );
  }
  private _normalizeValue(value: string | undefined): string | undefined {
    return value?.toLowerCase().replace(/\s/g, '');
  }

  onTagSelected(tag: ITag) {
    if (!this.canSubmit) return;
    if (tag) {
      this.form.get('tagControl')?.setValue('');
      const index = this.tags.find((anyTag) => anyTag.id === tag.id);
      if (!index) {
        this.form.get('tagControl')?.setValue('');
        this.tags = this.tags = [...this.tags, tag];
      }
    }
  }

  cancelTag(id: number) {
    if (!this.canSubmit) return;
    this.tags = this.tags.filter((tag) => tag.id !== id)
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canSubmit) return;
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
    if (!this.canSubmit) return;

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(files);
    }
  }
  onFileChange(event: Event) {
    if (!this.canSubmit) return;
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files) {
      this.processFiles(files);
      input.value = '';
    }
  }

  processFiles(files: FileList) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const dataUrl = e.target.result
      this.uploadedImage = dataUrl.split(',')[1];
    };
    reader.readAsDataURL(file);
  }

  cancelImage(){
    if (!this.canSubmit) return;
    this.uploadedImage = ''
    this.displayedImage = ''
  }

  getImage(): string{
    return this.baseUrl + this.displayedImage + '?timestamp=' + this.timestamp
  }

  save(): boolean | void{
    if (!this.canSubmit) return;
    if(this.form.invalid){
      this.form.markAllAsTouched()
      return false
    }

    const tag_ids: number[] = []
    this.tags.forEach(tag => {
      tag_ids.push(tag.id)
    });

    const payload : Omit<IBrand, 'id'>  = {
      code: this.form.get('code')?.value,
      name: this.form.get('name')?.value,
      description: this.form.get('description')?.value,
      tag_ids: tag_ids,
      image: this.uploadedImage,
    }
    if(this.data) {
      this.store.dispatch(UpdateBrand({id: this.data.id, brand: payload}))
    } else {
      this.store.dispatch(AddBrand({brand: payload}))
    }
  }

  submitPermission() {
    return { module: 'product', action: this.isUpdate ? 'edit' : 'create' };
  }

  closed(){
    this.dialogRef.close()
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
